import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiBell,
  FiCheckCircle,
  FiEdit3,
  FiExternalLink,
  FiFlag,
  FiMoreVertical,
  FiPlusCircle,
  FiShield,
  FiTrendingUp,
  FiUserCheck,
} from "react-icons/fi";

import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsEnterpriseForm } from "@components/forms/index.js";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridFilterControl,
} from "@components/data-grid/index.js";
import { requireCmtsUiAction } from "@cmts/utils/cmtsActionGuards.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

type EscalationRow = Record<string, unknown>;
type CmtsFormOption = { readonly value: string; readonly label: string };
type ActionTone = "blue" | "slate" | "cyan" | "green" | "rose";
type GovernedAction = "acknowledge" | "resolve" | "escalate";

type PendingGovernedAction = {
  readonly code: GovernedAction;
  readonly row: EscalationRow;
};

const DEFAULT_ESCALATION_TYPES: readonly CmtsFormOption[] = [
  { value: "SLA_AT_RISK", label: "SLA at risk" },
  { value: "SLA_BREACHED", label: "SLA breached" },
  { value: "MANUAL", label: "Manual escalation" },
  { value: "REOPENED", label: "Complaint reopened" },
];

function text(row: EscalationRow, keys: readonly string[], fallback = ""): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }
  return fallback;
}

function normalizeCode(value: unknown): string {
  return String(value ?? "").trim().replace(/[\s-]+/g, "_").toUpperCase();
}

function displayCode(value: unknown, fallback = "-"): string {
  const normalized = normalizeCode(value);
  if (!normalized) return fallback;
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function statusCode(row: EscalationRow): string {
  return normalizeCode(row.status ?? row.state ?? row.alertStatus ?? "OPEN");
}

function priorityCode(row: EscalationRow): string {
  return normalizeCode(row.priority ?? row.priorityCode ?? row.severity ?? "HIGH");
}

function levelLabel(row: EscalationRow): string {
  const value = text(row, ["escalationLevel", "levelNumber", "level"], "1");
  return /^level/i.test(value) ? value : `Level ${value}`;
}

function isAcknowledged(row: EscalationRow): boolean {
  return statusCode(row) === "ACKNOWLEDGED";
}

function isResolved(row: EscalationRow): boolean {
  return ["RESOLVED", "CLOSED"].includes(statusCode(row));
}

function dateLabel(value: unknown): string {
  if (!value) return "-";
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value).split("T")[0] ?? "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function toDateTimeLocal(value: unknown): string {
  if (!value) return "";
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return "";
  const offset = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offset).toISOString().slice(0, 16);
}

function buildEscalationFields(options: {
  readonly escalationType?: readonly CmtsFormOption[] | undefined;
  readonly complaints?: readonly CmtsFormOption[] | undefined;
  readonly owners?: readonly CmtsFormOption[] | undefined;
  readonly levels?: readonly CmtsFormOption[] | undefined;
}) {
  return [
    {
      name: "complaintId",
      label: "Linked complaint",
      type: "select" as const,
      required: true,
      options: options.complaints ?? [],
      placeholder: "Select complaint",
      help: "The complaint supplies priority, department and traceability context.",
    },
    {
      name: "escalationType",
      label: "Escalation trigger",
      type: "select" as const,
      required: true,
      options: options.escalationType ?? [],
      placeholder: "Select escalation trigger",
      help: "Select the governed reason that raised this escalation.",
    },
    {
      name: "escalationLevel",
      label: "Escalation level",
      type: "select" as const,
      required: true,
      options: options.levels ?? [],
      placeholder: "Select level",
    },
    {
      name: "ownerId",
      label: "Recipient owner",
      type: "select" as const,
      required: true,
      options: options.owners ?? [],
      placeholder: "Select recipient owner",
    },
    {
      name: "reason",
      label: "Escalation reason",
      type: "textarea" as const,
      required: true,
      minLength: 6,
      placeholder: "Explain the risk, breach or management attention required",
    },
    {
      name: "dueDate",
      label: "Target response date/time",
      type: "datetime-local" as const,
      required: true,
    },
    {
      name: "remarks",
      label: "Additional remarks",
      type: "textarea" as const,
      placeholder: "Optional evidence, dependency or follow-up note",
    },
  ] as const;
}

function defaultEscalation(
  row: EscalationRow | null,
  escalationTypes: readonly CmtsFormOption[],
): Record<string, unknown> {
  if (row) {
    return {
      ...row,
      complaintId: text(row, ["complaintId", "linkedEntityId"]),
      escalationType:
        text(row, ["escalationType", "triggerTypeCode", "triggerTypeId"]) ||
        escalationTypes[0]?.value ||
        "MANUAL",
      escalationLevel: text(row, ["escalationLevel", "levelNumber"], "1"),
      ownerId: text(row, ["ownerId", "recipientUserId", "targetUserId"]),
      reason: text(row, ["reason", "summary", "description"]),
      dueDate: toDateTimeLocal(row.dueDate),
    };
  }
  return {
    escalationType: escalationTypes[0]?.value ?? "MANUAL",
    escalationLevel: "1",
    dueDate: toDateTimeLocal(new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString()),
  };
}

function initialGridStateFromUrl(params: URLSearchParams): {
  filters: Record<string, string>;
  searchText: string;
} {
  const filters: Record<string, string> = {};
  const searchText = params.get("q") ?? params.get("search") ?? "";
  const aliases: Record<string, string> = {
    owner: "ownerName",
    assignee: "ownerName",
    statusGroup: "status",
    level: "escalationLevel",
  };
  params.forEach((value, key) => {
    if (!["q", "search"].includes(key) && value) {
      filters[aliases[key] ?? key] = decodeURIComponent(value)
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase();
    }
  });
  return { filters, searchText };
}

function EscalationActionCard(props: {
  readonly icon: ReactNode;
  readonly tone: ActionTone;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cmts-escalation-action-card cmts-escalation-action-card--${props.tone}`}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-escalation-action-card__icon">{props.icon}</span>
      <span className="cmts-escalation-action-card__copy">
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>
      <span className="cmts-escalation-action-card__arrow">›</span>
    </button>
  );
}

export function CmtsEscalationPage(props: {
  readonly queue?: readonly EscalationRow[] | undefined;
  readonly reminders?: readonly EscalationRow[] | undefined;
  readonly diagnostics?: readonly ComponentDiagnostic[] | undefined;
  readonly onOpenEscalation?: ((row: EscalationRow) => void | Promise<void>) | undefined;
  readonly onAcknowledge?: ((row: EscalationRow) => void | Promise<void>) | undefined;
  readonly onResolve?: ((row: EscalationRow) => void | Promise<void>) | undefined;
  readonly onEscalate?: ((row: EscalationRow) => void | Promise<void>) | undefined;
  readonly onCreateEscalation?: ((payload: EscalationRow) => void | Promise<void>) | undefined;
  readonly onUpdateEscalation?: ((row: EscalationRow, payload: EscalationRow) => void | Promise<void>) | undefined;
  readonly masterOptions?: {
    readonly escalationType?: readonly CmtsFormOption[] | undefined;
    readonly complaints?: readonly CmtsFormOption[] | undefined;
    readonly owners?: readonly CmtsFormOption[] | undefined;
    readonly levels?: readonly CmtsFormOption[] | undefined;
  } | undefined;
}) {
  const rows = props.queue ?? [];
  const reminders = props.reminders ?? [];
  const configuredEscalationTypes = props.masterOptions?.escalationType ?? [];
  const escalationTypes = configuredEscalationTypes.length > 0
    ? configuredEscalationTypes
    : DEFAULT_ESCALATION_TYPES;
  const [searchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<EscalationRow | null>(null);
  const [actionsFor, setActionsFor] = useState<EscalationRow | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingGovernedAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const initialGridState = useMemo(() => initialGridStateFromUrl(searchParams), [searchParams]);
  const summary = useMemo(() => ({
    total: rows.length,
    open: rows.filter((row) => statusCode(row) === "OPEN").length,
    acknowledged: rows.filter(isAcknowledged).length,
    resolved: rows.filter(isResolved).length,
    critical: rows.filter((row) => priorityCode(row) === "CRITICAL").length,
  }), [rows]);
  const fields = useMemo(
    () => buildEscalationFields({
      escalationType: escalationTypes,
      complaints: props.masterOptions?.complaints ?? [],
      owners: props.masterOptions?.owners ?? [],
      levels: props.masterOptions?.levels ?? [],
    }),
    [escalationTypes, props.masterOptions?.complaints, props.masterOptions?.owners, props.masterOptions?.levels],
  );
  const ownerOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => text(row, ["ownerName"], "")).filter(Boolean)))
      .map((label) => ({ value: label, label })),
    [rows],
  );

  const columns = useMemo<readonly CmtsGridColumn<EscalationRow>[]>(() => [
    {
      id: "title",
      header: "Escalation",
      sortable: true,
      filterable: true,
      minWidth: 260,
      render: (row) => (
        <span className="cmts-grid-title-cell">
          <strong>{text(row, ["title", "summary"], "Escalation")}</strong>
          <small>{text(row, ["reason", "description"], "Management attention required")}</small>
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      filterable: true,
      minWidth: 125,
      render: (row) => {
        const code = statusCode(row);
        return <span className={`cmts-escalation-status-chip cmts-escalation-status-chip--${code.toLowerCase()}`}>{displayCode(code)}</span>;
      },
    },
    {
      id: "priority",
      header: "Priority",
      sortable: true,
      filterable: true,
      minWidth: 115,
      render: (row) => {
        const code = priorityCode(row);
        return <span className={`cmts-priority-chip cmts-priority-chip--${code.toLowerCase()}`}>{displayCode(code)}</span>;
      },
    },
    { id: "escalationLevel", header: "Level", sortable: true, filterable: true, minWidth: 100, render: levelLabel },
    { id: "ownerName", header: "Owner", sortable: true, filterable: true, minWidth: 160, render: (row) => text(row, ["ownerName"], "Unassigned") },
    { id: "complaintNumber", header: "Complaint", sortable: true, filterable: true, minWidth: 165, render: (row) => <strong className="cmts-escalation-complaint-code">{text(row, ["complaintNumber", "complaintCode"], "-")}</strong> },
    { id: "dueDate", header: "Response due", sortable: true, filterable: true, minWidth: 170, render: (row) => dateLabel(row.dueDate ?? row.triggeredAt) },
    {
      id: "escalationActions",
      header: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 125,
      render: (row) => (
        <button
          type="button"
          className="cmts-notification-actions-button"
          onClick={(event) => {
            event.stopPropagation();
            setError(null);
            setActionsFor(row);
          }}
        >
          <span className="cmts-notification-actions-button__icon"><FiMoreVertical /></span>
          <span>Actions</span>
        </button>
      ),
    },
  ], []);

  const filters = useMemo<readonly CmtsGridFilterControl<EscalationRow>[]>(() => [
    { id: "status", label: "Status", columnId: "status", operator: "equals", placeholder: "All statuses", options: [{ value: "open", label: "Open" }, { value: "acknowledged", label: "Acknowledged" }, { value: "resolved", label: "Resolved" }] },
    { id: "priority", label: "Priority", columnId: "priority", operator: "equals", placeholder: "All priorities", options: [{ value: "high", label: "High" }, { value: "critical", label: "Critical" }] },
    { id: "escalationLevel", label: "Level", columnId: "escalationLevel", operator: "contains", placeholder: "All levels", options: props.masterOptions?.levels ?? [] },
    { id: "ownerName", label: "Owner", columnId: "ownerName", operator: "equals", placeholder: "All owners", options: ownerOptions },
  ], [ownerOptions, props.masterOptions?.levels]);

  const runAction = async (
    handler: ((row: EscalationRow) => void | Promise<void>) | undefined,
    handlerName: string,
    row: EscalationRow,
    fallback: string,
  ) => {
    try {
      setBusy(true);
      setError(null);
      setSuccessMessage(null);
      await requireCmtsUiAction(handler, handlerName)(row);
      setActionsFor(null);
    } catch (caught) {
      setError(normalizeUserSafeApiError(caught).userMessage || fallback);
    } finally {
      setBusy(false);
    }
  };

  const executeGovernedAction = async () => {
    if (!pendingAction || busy) return;

    const action = pendingAction.code;
    const handler = action === "acknowledge"
      ? props.onAcknowledge
      : action === "resolve"
        ? props.onResolve
        : props.onEscalate;
    const handlerName = action === "acknowledge"
      ? "onAcknowledge"
      : action === "resolve"
        ? "onResolve"
        : "onEscalate";
    const success = action === "acknowledge"
      ? "Escalation acknowledged successfully."
      : action === "resolve"
        ? "Escalation resolved successfully."
        : "Next escalation level created successfully.";

    try {
      setBusy(true);
      setError(null);
      setSuccessMessage(null);
      await requireCmtsUiAction(handler, handlerName)(pendingAction.row);
      setPendingAction(null);
      setActionsFor(null);
      setSuccessMessage(success);
    } catch (caught) {
      setError(
        normalizeUserSafeApiError(caught).userMessage ||
          `Unable to ${action} escalation.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const openCreate = () => {
    setSelected(null);
    setError(null);
    setSuccessMessage(null);
    setFormOpen(true);
  };
  const openEdit = (row: EscalationRow) => {
    setSelected(row);
    setActionsFor(null);
    setError(null);
    setSuccessMessage(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (!busy) {
      setSelected(null);
      setFormOpen(false);
      setError(null);
    }
  };

  return (
    <PageContainer title="Escalation Management">
      {(props.diagnostics ?? []).length > 0 ? <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} /> : null}
      {successMessage ? (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #a7f3d0",
            borderRadius: 10,
            color: "#047857",
            background: "#ecfdf5",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <FiCheckCircle style={{ marginRight: 7, verticalAlign: "-2px" }} />
          {successMessage}
        </div>
      ) : null}

      <section className="cmts-escalation-page-header">
        <div>
          <p className="cmts-escalation-page-header__eyebrow">Governed response</p>
          <h2>Escalation operations workspace</h2>
          <p>Track SLA risks, ownership, acknowledgement, resolution and management escalation from one queue.</p>
        </div>
        <Button className="cmts-escalation-create-button" onClick={openCreate} disabled={busy}>
          <FiPlusCircle /> Create escalation
        </Button>
      </section>

      <section className="cmts-escalation-summary-grid" aria-label="Escalation summary">
        <article className="cmts-escalation-summary-card cmts-escalation-summary-card--total"><span><FiFlag /></span><div><small>Total</small><strong>{summary.total}</strong></div></article>
        <article className="cmts-escalation-summary-card cmts-escalation-summary-card--open"><span><FiAlertCircle /></span><div><small>Open</small><strong>{summary.open}</strong></div></article>
        <article className="cmts-escalation-summary-card cmts-escalation-summary-card--ack"><span><FiUserCheck /></span><div><small>Acknowledged</small><strong>{summary.acknowledged}</strong></div></article>
        <article className="cmts-escalation-summary-card cmts-escalation-summary-card--resolved"><span><FiCheckCircle /></span><div><small>Resolved</small><strong>{summary.resolved}</strong></div></article>
        <article className="cmts-escalation-summary-card cmts-escalation-summary-card--critical"><span><FiAlertTriangle /></span><div><small>Critical</small><strong>{summary.critical}</strong></div></article>
      </section>

      <CmtsSmartDataGrid<EscalationRow>
        title="Escalation queue"
        subtitle="Review management attention, SLA risk and accountable ownership."
        rows={rows}
        columns={columns}
        filters={filters}
        initialFilterValues={initialGridState.filters}
        initialSearchText={initialGridState.searchText}
        rowKey="id"
        defaultPageSize={10}
        filterSummary={["Tenant/persona scope applied", "Backend lifecycle state", "Audited governed actions"]}
        onRowClick={props.onOpenEscalation}
        emptyMessage="No escalation records are available for this persona."
      />

      <section className="cmts-escalation-insight-grid">
        <article><span><FiShield /></span><div><strong>Governed escalation</strong><small>Trigger, recipient, level and audit history remain backend-authoritative.</small></div></article>
        <article><span><FiBell /></span><div><strong>Reminder focus</strong><small>{reminders.length} reminder records currently require review.</small></div></article>
      </section>

      {actionsFor ? (
        <CmtsEnterpriseModal open={true} title="Escalation actions" subtitle={text(actionsFor, ["title"], "Escalation")} size="md" onClose={() => !busy && setActionsFor(null)}>
          <div className="cmts-escalation-actions-modal" style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
            {error ? <div className="cmts-escalation-inline-error">{error}</div> : null}
            <section className="cmts-escalation-action-summary">
              <div><small>Status</small><strong>{displayCode(statusCode(actionsFor))}</strong></div>
              <div><small>Priority</small><strong>{displayCode(priorityCode(actionsFor))}</strong></div>
              <div><small>Level</small><strong>{levelLabel(actionsFor)}</strong></div>
              <div><small>Owner</small><strong>{text(actionsFor, ["ownerName"], "Unassigned")}</strong></div>
            </section>
            <div className="cmts-escalation-actions-grid">
              <EscalationActionCard icon={<FiExternalLink />} tone="blue" title="Open complaint" description="Open the linked complaint workflow." disabled={busy || !props.onOpenEscalation} onClick={() => void runAction(props.onOpenEscalation, "onOpenEscalation", actionsFor, "Unable to open complaint.")} />
              <EscalationActionCard icon={<FiEdit3 />} tone="slate" title="Edit escalation" description="Update recipient and escalation notes." disabled={busy || !props.onUpdateEscalation} onClick={() => openEdit(actionsFor)} />
              <EscalationActionCard icon={<FiUserCheck />} tone="cyan" title="Acknowledge" description="Review details and confirm acknowledgement." disabled={busy || !props.onAcknowledge || isAcknowledged(actionsFor) || isResolved(actionsFor)} onClick={() => { setError(null); setPendingAction({ code: "acknowledge", row: actionsFor }); setActionsFor(null); }} />
              <EscalationActionCard icon={<FiCheckCircle />} tone="green" title="Resolve" description="Review details and confirm resolution." disabled={busy || !props.onResolve || isResolved(actionsFor)} onClick={() => { setError(null); setPendingAction({ code: "resolve", row: actionsFor }); setActionsFor(null); }} />
              <EscalationActionCard icon={<FiTrendingUp />} tone="rose" title="Escalate further" description="Review and create the next governed level." disabled={busy || !props.onEscalate || isResolved(actionsFor)} onClick={() => { setError(null); setPendingAction({ code: "escalate", row: actionsFor }); setActionsFor(null); }} />
            </div>
          </div>
        </CmtsEnterpriseModal>
      ) : null}

      {pendingAction ? (
        <CmtsEnterpriseModal
          open={true}
          title={
            pendingAction.code === "acknowledge"
              ? "Acknowledge escalation"
              : pendingAction.code === "resolve"
                ? "Resolve escalation"
                : "Escalate to next level"
          }
          subtitle="Review the governed action before confirming."
          size="md"
          onClose={() => {
            if (!busy) {
              setPendingAction(null);
              setError(null);
            }
          }}
        >
          <div style={{ width: "100%", minWidth: 0 }}>
            {error ? <div className="cmts-escalation-inline-error">{error}</div> : null}

            <section
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 14,
                padding: 14,
                border: `1px solid ${pendingAction.code === "resolve" ? "#a7f3d0" : pendingAction.code === "escalate" ? "#fecdd3" : "#a5f3fc"}`,
                borderRadius: 12,
                background: pendingAction.code === "resolve" ? "#ecfdf5" : pendingAction.code === "escalate" ? "#fff1f2" : "#ecfeff",
              }}
            >
              <span
                style={{
                  width: 38,
                  height: 38,
                  display: "grid",
                  flex: "0 0 38px",
                  placeItems: "center",
                  borderRadius: 10,
                  color: pendingAction.code === "resolve" ? "#047857" : pendingAction.code === "escalate" ? "#be123c" : "#0e7490",
                  background: "#fff",
                }}
              >
                {pendingAction.code === "resolve" ? <FiCheckCircle /> : pendingAction.code === "escalate" ? <FiTrendingUp /> : <FiUserCheck />}
              </span>
              <div>
                <strong style={{ display: "block", marginBottom: 4, color: "#1e293b", fontSize: 13 }}>
                  {pendingAction.code === "acknowledge"
                    ? "Confirm escalation review"
                    : pendingAction.code === "resolve"
                      ? "Confirm escalation closure"
                      : "Create the next escalation level"}
                </strong>
                <small style={{ color: "#64748b", lineHeight: 1.5 }}>
                  {pendingAction.code === "acknowledge"
                    ? "The escalation will move to Acknowledged and an audit event will be recorded."
                    : pendingAction.code === "resolve"
                      ? "The escalation will move to Resolved and further actions will be disabled."
                      : "A new governed escalation will be created at the next level for the same complaint."}
                </small>
              </div>
            </section>

            <section className="cmts-escalation-action-summary" style={{ width: "100%", boxSizing: "border-box" }}>
              <div><small>Escalation</small><strong>{text(pendingAction.row, ["title"], "Escalation")}</strong></div>
              <div><small>Current status</small><strong>{displayCode(statusCode(pendingAction.row))}</strong></div>
              <div><small>Level</small><strong>{levelLabel(pendingAction.row)}</strong></div>
              <div><small>Owner</small><strong>{text(pendingAction.row, ["ownerName"], "Unassigned")}</strong></div>
            </section>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <Button
                label="Cancel"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setPendingAction(null);
                  setError(null);
                }}
              />
              <Button
                label={
                  busy
                    ? "Processing..."
                    : pendingAction.code === "acknowledge"
                      ? "Confirm acknowledgement"
                      : pendingAction.code === "resolve"
                        ? "Confirm resolution"
                        : "Create next level"
                }
                variant={pendingAction.code === "resolve" ? "primary" : pendingAction.code === "escalate" ? "danger" : "primary"}
                loading={busy}
                disabled={busy}
                onClick={() => void executeGovernedAction()}
              />
            </div>
          </div>
        </CmtsEnterpriseModal>
      ) : null}

      <CmtsEnterpriseModal open={formOpen} title={selected ? "Edit escalation" : "Create escalation"} subtitle="Create a complaint-linked governed escalation." size="lg" onClose={closeForm}>
        <div className="cmts-escalation-form-modal--create">
          <section className="cmts-escalation-form-hero">
            <span className="cmts-escalation-form-hero__icon"><FiShield /></span>
            <div className="cmts-escalation-form-hero__copy">
              <small>Governed workflow</small>
              <strong>{selected ? "Update escalation ownership" : "Raise a new escalation"}</strong>
              <p>Connect a complaint, choose the governed trigger, assign accountable ownership and set the response commitment.</p>
            </div>
            <span className="cmts-escalation-form-hero__status">Audit enabled</span>
          </section>
          <section className="cmts-escalation-form-steps" aria-label="Escalation creation steps">
            <article>
              <span>01</span>
              <div><strong>Complaint context</strong><small>Complaint and trigger</small></div>
            </article>
            <article>
              <span>02</span>
              <div><strong>Ownership</strong><small>Level and recipient</small></div>
            </article>
            <article>
              <span>03</span>
              <div><strong>Response plan</strong><small>Reason, target and notes</small></div>
            </article>
          </section>
          <section className="cmts-escalation-form-heading">
            <span><FiFlag /></span>
            <div>
              <strong>Escalation details</strong>
              <small>Required fields are marked with an asterisk. Priority and department come from the linked complaint.</small>
            </div>
            <em>{selected ? "Edit mode" : "New record"}</em>
          </section>
          <CmtsEnterpriseForm
            fields={fields}
            initialValues={defaultEscalation(selected, escalationTypes)}
            submitLabel={selected ? "Save escalation" : "Create escalation"}
            busy={busy}
            error={error}
            onCancel={closeForm}
            onSubmit={async (payload) => {
              try {
                setBusy(true);
                setError(null);
                if (selected) {
                  await requireCmtsUiAction(props.onUpdateEscalation, "onUpdateEscalation")(selected, payload);
                } else {
                  await requireCmtsUiAction(props.onCreateEscalation, "onCreateEscalation")(payload);
                }
                setSelected(null);
                setFormOpen(false);
                setSuccessMessage(selected ? "Escalation updated successfully." : "Escalation created successfully.");
              } catch (caught) {
                setError(normalizeUserSafeApiError(caught).userMessage || "Escalation save failed.");
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}